import type React from "react"
import { useState } from "react"

import { ChevronDown, ChevronUp } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { shortenFundingValue } from "@/lib/forms/utils"
import { cn } from "@/lib/utils"

import type { Product } from "@boundless/bl-api-client/common/models/Product"
import type { LenderProfile } from "@boundless/bl-api-client/customer/models"

// NOTE: This component is also rendered in the Django admin Lender Profile
// preview via the <boundless-lender-card> widget. After changing it, rebuild
// that bundle with `mise run fe:build:lender-card` (the committed
// apps/lender/static/lender/js/lender-card.js will otherwise go stale).
interface LenderCardProps {
  lender: LenderProfile
  isSelected: boolean
  onSelect: (id: number) => void
  products?: Product[]
}

export function LenderCard({
  lender,
  isSelected,
  onSelect,
  products,
}: LenderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleCardClick = () => {
    onSelect(lender.id)
  }

  const handleViewMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const productNames = products
    ?.filter((product: Product) => lender.products.includes(product.id))
    .map((product: Product) => product.name)

  const highlights = [
    lender.highlight1,
    lender.highlight2,
    lender.highlight3,
    lender.highlight4,
    lender.highlight5,
  ].filter((x) => x)

  return (
    <Card
      className={cn(
        "cursor-pointer rounded-xl border transition-all duration-200 hover:shadow-md",
        isSelected
          ? "ring-primary border-primary bg-primary/5 ring-1"
          : "hover:border-primary/50 border-gray-200",
        lender.isDealDesk && "bg-gray-200",
      )}
      onClick={handleCardClick}
    >
      <CardContent className="grid gap-4 p-4 md:p-6 lg:grid-cols-[1fr_1px_3fr_1fr] lg:gap-6">
        {/* Left - Logo + info */}
        <div className="grid gap-3 lg:col-span-1">
          {lender.logo ? (
            <img
              src={lender.logo}
              alt={`${lender.name} logo`}
              className="h-auto max-h-20 w-full object-contain lg:mx-auto lg:max-h-12"
            />
          ) : (
            <h3 className="text-base leading-tight font-semibold text-gray-900 lg:text-lg">
              {lender.name}
            </h3>
          )}

          <div className="lg:text-center">
            <p className="text-xs text-gray-600 md:text-sm lg:text-balance">
              {lender.subheader}
            </p>
          </div>
        </div>

        {/* Divider (lg+) */}
        <div className="hidden h-full w-px bg-gray-200 lg:block" />

        {/* Main - stats + products */}
        <div className="grid min-w-0 gap-3 lg:col-span-1">
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3 lg:gap-8">
            <div>
              <p className="mb-1 text-xs font-medium text-gray-600 md:text-sm">
                Funding Amount:
              </p>
              <p className="text-sm font-semibold text-gray-900">
                ${shortenFundingValue(lender.fundingAmountMin)} - $
                {shortenFundingValue(lender.fundingAmountMax)}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-600 md:text-sm">
                Decision Time:
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {lender.decisionTime}
              </p>
            </div>

            <div className="col-span-2 lg:col-span-1">
              <p className="mb-1 text-xs font-medium text-gray-600 md:text-sm">
                Cost of Capital:
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {lender.startingFrom}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {productNames?.map((name: string) => (
              <Badge
                key={name}
                variant="secondary"
                className="border-primary text-primary border-1 bg-white text-xs"
              >
                {name}
              </Badge>
            ))}
          </div>
        </div>

        {/* Right - actions */}
        <div className="grid grid-cols-2 gap-2 lg:col-span-1 lg:grid-cols-1 lg:content-start">
          {lender.easyApplyEnabled && (
            <Button type="submit" className="w-full">
              Easy Apply
            </Button>
          )}

          {highlights.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleViewMoreClick}
              className="text-primary hover:text-primary w-full px-3 py-2"
            >
              <span className="mr-1 text-sm font-medium">
                {isExpanded ? "Show Less" : "View More"}
              </span>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>

        {/* Expanded - full width */}
        {isExpanded && highlights.length > 0 && (
          <div className="col-span-full border-t border-gray-200 pt-4">
            <ul className="space-y-3">
              {highlights.map((name, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="bg-primary mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                  <p className="text-sm leading-relaxed text-gray-600">
                    {name}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
